import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLiveTime, formatNowClock } from '../hooks/useLiveTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  awaiting_confirmation: { label: 'Təsdiq gözləyir', color: 'bg-rose-100 text-rose-700' },
  pending: { label: 'Yeni', color: 'bg-amber-100 text-amber-700' },
  preparing: { label: 'Hazırlanır', color: 'bg-blue-100 text-blue-700' },
  ready: { label: 'Hazır', color: 'bg-emerald-100 text-emerald-700' },
  served: { label: 'Verildi', color: 'bg-stone-100 text-stone-700' },
  completed: { label: 'Tamamlandı', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Ləğv', color: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rədd', color: 'bg-red-100 text-red-700' },
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('az-AZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const todayIso = () => {
  const d = new Date();
  d.setHours(d.getHours() + 4); // Baku UTC+4
  return d.toISOString().slice(0, 10);
};
const daysAgoIso = (n) => {
  const d = new Date();
  d.setHours(d.getHours() + 4);
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export const OwnerAnalytics = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [startDate, setStartDate] = useState(daysAgoIso(6));
  const [endDate, setEndDate] = useState(todayIso());
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restaurantName, setRestaurantName] = useState('');
  const nowMs = useLiveTime(1000);
  const clock = formatNowClock(nowMs);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, startDate, endDate]);

  const fetchData = async () => {
    try {
      const params = `start=${startDate}&end=${endDate}`;
      const [a, allOrders, r] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/orders/restaurant/${restaurantId}/analytics?${params}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/orders/restaurant/${restaurantId}?limit=500`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, { withCredentials: true }),
      ]);
      setAnalytics(a.data);
      setOrders(allOrders.data);
      setRestaurantName(r.data.name);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/orders/restaurant/${restaurantId}/export?start=${startDate}&end=${endDate}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${restaurantName.replace(/\s+/g, '_')}_${startDate}_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Excel yüklənə bilmədi');
    } finally {
      setDownloading(false);
    }
  };

  const applyQuickRange = (days) => {
    setStartDate(daysAgoIso(days - 1));
    setEndDate(todayIso());
  };

  if (loading || !analytics) {
    return <div className="min-h-screen flex items-center justify-center bg-stone-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div>;
  }

  const sym = analytics.currency_symbol;
  const maxHourlyOrders = Math.max(...analytics.today_hourly.map(h => h.orders), 1);
  const maxDayRevenue = Math.max(...analytics.by_day.map(d => d.revenue), 1);

  // Today's orders (from Baku day)
  const now = new Date();
  const bakuOffset = 4 * 60; // minutes
  const localOffset = -now.getTimezoneOffset();
  const bakuNow = new Date(now.getTime() + (bakuOffset - localOffset) * 60000);
  const bakuTodayStr = bakuNow.toISOString().slice(0, 10);

  const todayOrders = orders.filter((o) => {
    if (!o.created_at) return false;
    const bd = new Date(new Date(o.created_at).getTime() + (bakuOffset - localOffset) * 60000);
    return bd.toISOString().slice(0, 10) === bakuTodayStr;
  });

  return (
    <div className="min-h-screen bg-stone-50 py-6">
      <div className="max-w-7xl mx-auto px-4">
        <button onClick={() => navigate('/dashboard')} className="text-emerald-600 hover:underline mb-4" data-testid="analytics-back">← Dashboard</button>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h1 className="text-3xl font-black text-stone-900" data-testid="analytics-title">📊 Analitika</h1>
            <p className="text-sm text-stone-500 mt-1">{restaurantName} • Baku vaxtı (canlı yenilənir)</p>
          </div>
          <div
            className="bg-white border border-stone-200 rounded-2xl px-4 py-2.5 shadow-sm flex items-center gap-3"
            data-testid="live-clock"
          >
            <div className="text-2xl">🕐</div>
            <div>
              <div className="text-xs text-stone-500 uppercase font-semibold">Cari vaxt (Baku)</div>
              <div className="font-mono text-lg font-bold text-stone-900" data-testid="live-clock-time">{clock.time}</div>
              <div className="text-xs text-stone-500" data-testid="live-clock-date">{clock.date}</div>
            </div>
          </div>
        </div>

        {/* Date range picker */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-5 shadow-sm" data-testid="date-range-picker">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Başlanğıc</label>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border-2 border-stone-200 rounded-lg text-sm font-mono focus:border-emerald-500 focus:outline-none"
                data-testid="range-start-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Bitiş</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={todayIso()}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border-2 border-stone-200 rounded-lg text-sm font-mono focus:border-emerald-500 focus:outline-none"
                data-testid="range-end-input"
              />
            </div>
            <div className="flex items-center gap-1.5 border-l border-stone-200 pl-3 ml-1">
              <span className="text-xs text-stone-400 mr-1">Sürətli:</span>
              {[
                { d: 1, l: 'Bu gün' },
                { d: 7, l: '7 gün' },
                { d: 30, l: '30 gün' },
                { d: 90, l: '90 gün' },
              ].map((p) => (
                <button
                  key={p.d}
                  onClick={() => applyQuickRange(p.d)}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-stone-200 bg-stone-50 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition"
                  data-testid={`quick-${p.d}d`}
                >{p.l}</button>
              ))}
            </div>
            <div className="ml-auto">
              <button
                onClick={downloadExcel}
                disabled={downloading}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50 flex items-center gap-1.5"
                data-testid="download-excel-btn"
              >{downloading ? '...' : '📥 Excel yüklə'}</button>
            </div>
          </div>
          <div className="text-xs text-stone-500 mt-2" data-testid="range-summary">
            Aralıq: <strong>{startDate}</strong> → <strong>{endDate}</strong> ({analytics.range_days} gün)
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI label="Ümumi sifariş" value={analytics.total.orders} color="from-stone-700 to-stone-900" testid="kpi-orders" />
          <KPI label="Qəbul olunan" value={analytics.total.accepted} color="from-emerald-600 to-emerald-700" testid="kpi-accepted" />
          <KPI label="Ləğv/Rədd" value={analytics.total.cancelled + analytics.total.rejected} color="from-red-500 to-red-600" testid="kpi-cancelled" />
          <KPI label="Ümumi gəlir" value={`${sym}${analytics.total.revenue.toFixed(2)}`} color="from-amber-500 to-amber-600" testid="kpi-revenue" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
          {/* Bu gün saatlıq */}
          <Card title="⏰ Bu gün — saatlıq bölgü" testId="today-hourly">
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {analytics.today_hourly.filter(h => h.orders > 0).length === 0 ? (
                <p className="text-sm text-stone-500 py-4 text-center">Bu gün hələ sifariş yoxdur</p>
              ) : (
                analytics.today_hourly.filter(h => h.orders > 0).map((h) => (
                  <div key={h.hour} className="flex items-center gap-2 text-sm">
                    <span className="w-12 text-stone-500 font-mono text-xs">{String(h.hour).padStart(2, '0')}:00</span>
                    <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden relative">
                      <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${(h.orders / maxHourlyOrders) * 100}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-stone-900">{h.orders}</span>
                    </div>
                    <span className="w-20 text-right font-semibold text-emerald-700">{sym}{h.revenue.toFixed(0)}</span>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Günlərə görə */}
          <Card title={`📅 ${analytics.range_days} gün — gəlir`} testId="by-day">
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {analytics.by_day.length === 0 ? (
                <p className="text-sm text-stone-500 py-4 text-center">Məlumat yoxdur</p>
              ) : (
                analytics.by_day.map((d) => (
                  <div key={d.date} className="flex items-center gap-2 text-sm">
                    <span className="w-24 text-stone-500 font-mono text-xs">{d.date}</span>
                    <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden relative">
                      <div className="bg-amber-500 h-full rounded-full transition-all" style={{ width: `${(d.revenue / maxDayRevenue) * 100}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold text-stone-900">{d.orders} sifariş</span>
                    </div>
                    <span className="w-24 text-right font-bold text-amber-700">{sym}{d.revenue.toFixed(2)}</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Top items */}
        <Card title="🏆 Ən çox satılan məhsullar" testId="top-items">
          {analytics.top_items.length === 0 ? (
            <p className="text-sm text-stone-500">Məlumat yoxdur</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {analytics.top_items.map((it, i) => (
                <div key={it.name} className="flex items-center gap-3 bg-stone-50 rounded-lg p-2.5">
                  <span className="w-7 h-7 flex items-center justify-center bg-emerald-100 text-emerald-700 rounded-full font-bold text-sm">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-stone-900 truncate">{it.name}</p>
                    <p className="text-xs text-stone-500">{it.quantity} ədəd</p>
                  </div>
                  <span className="font-bold text-emerald-700 text-sm">{sym}{it.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Today's orders list */}
        <div className="mt-6 bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
          <h3 className="font-bold text-stone-900 mb-3">📋 Bu günkü sifariş cədvəli ({todayOrders.length})</h3>
          {todayOrders.length === 0 ? (
            <p className="text-sm text-stone-500 text-center py-6">Bu gün hələ sifariş yoxdur</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 text-xs uppercase text-stone-500">
                    <th className="text-left py-2 pr-3">Vaxt</th>
                    <th className="text-left py-2 pr-3">№</th>
                    <th className="text-left py-2 pr-3">Masa</th>
                    <th className="text-left py-2 pr-3">Məhsullar</th>
                    <th className="text-right py-2 pr-3">Cəm</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody data-testid="today-orders-table">
                  {todayOrders.slice(0, 50).map((o) => {
                    const cfg = STATUS_LABELS[o.status] || { label: o.status, color: 'bg-stone-100 text-stone-700' };
                    return (
                      <tr key={o.id} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-2 pr-3 font-mono text-xs text-stone-600">{formatDateTime(o.created_at)}</td>
                        <td className="py-2 pr-3 font-semibold">{o.order_number}</td>
                        <td className="py-2 pr-3"><span className="font-bold text-emerald-700">M{o.table_number}</span></td>
                        <td className="py-2 pr-3 text-xs text-stone-600 max-w-xs truncate">{o.items.map(i => `${i.quantity}x${i.name_az || ''}`).join(', ')}</td>
                        <td className="py-2 pr-3 text-right font-bold">{sym}{o.total.toFixed(2)}</td>
                        <td className="py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.color}`}>{cfg.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KPI = ({ label, value, color, testid }) => (
  <div className={`bg-gradient-to-br ${color} text-white rounded-2xl p-4 shadow-md`} data-testid={testid}>
    <div className="text-2xl md:text-3xl font-black" data-testid={`${testid}-value`}>{value}</div>
    <div className="text-xs opacity-90 uppercase mt-1 font-semibold">{label}</div>
  </div>
);

const Card = ({ title, children, testId }) => (
  <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5" data-testid={testId}>
    <h3 className="font-bold text-stone-900 mb-3 text-sm">{title}</h3>
    {children}
  </div>
);
